import S from 's-js';
import { IAjax } from 'lib/Ajax';
import UOM from 'lib/UOM';
import { MeasurementInFeetAndInches } from 'lib/MeasurementInFeetAndInches';
import nonuniqueIndex, { NonuniqueIndex } from 'lib/nonuniqueIndex';

import { Entrypoints } from 'core/app/entrypoints';

import { VesselSide, VesselSideDisplayName } from 'server/enums/BerthReservation';

import { BerthReservation } from 'data/models/BerthReservation';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { NullableMeasurement } from 'data/models/Measurement';
import { OriginalPilotOrder as ServerOriginalPilotOrder, OriginalPilotOrderOptions } from 'data/models/OriginalPilotOrder';
import { PilotJobOrder } from 'data/models/PilotJobOrder';
import { PortCall } from 'data/models/PortCall';
import { Vessel } from 'data/models/Vessel';

import { PilotJobOrderDetailsCache } from './cache';

export interface OriginalPilotOrderParams {
    pjo : PilotJobOrder;
    hideActionsMenu : () => void;
}

export type OriginalPilotOrder = ReturnType<typeof OriginalPilotOrder>;
export function OriginalPilotOrder(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : PilotJobOrderDetailsCache,
    { pjo, hideActionsMenu } : OriginalPilotOrderParams
) {
    const
        url = () => entrypoints.ViewOriginalPilotOrder({ PortCallId: pjo.PortCallId() }),
        client = ajax.Url<{}, OriginalPilotOrderOptions>(url),
        displayedOrder = S.value<ServerOriginalPilotOrder | null>(null),
        showOrderUnavailableMsg = S.value(false),
        loading = S.value(false);

    // For doing dev on UI/styling. I found this useful since otherwise this component's UI is
    // only rendered while the print dialog is open. To use:
    //
    // 1) Set a breakpoint on the statement after this one.
    // 2) Open the PJO details popup for a PJO.
    // 3) Your breakpoint will be hit. When that happens, run the following in your browser's dev
    //    console:
    //
    //    attachDevTools()
    //
    // 4) Continue from your breakpoint. Now, as long as that PJO is still open, you can run the
    //    following commands in your browser's dev console:
    //
    //    OriginalPilotOrderDevTools.ShowPrintView()
    //    OriginalPilotOrderDevTools.HidePrintView()
    //
    // 5) You may also find it helpful to emulate @media print. Instructions for how to do this
    //    in Chrome at https://stackoverflow.com/a/29962072. Summary:
    //       - Open dev tools
    //       - Open hamburger menu in top right corner (tooltip reads "Customize and control DevTools")
    //       - Go to More tools > Rendering
    //       - Find "Emulate CSS media" option (near bottom) and select "print"
    const attachDevTools = () => {
        (window as any).OriginalPilotOrderDevTools = {
            ShowPrintView: async () => displayedOrder(await LoadOriginalOrder()),
            HidePrintView: () => displayedOrder(null)
        };
    };

    return {
        PilotAgency: () => pjo.PilotAgency(),
        DisplayedOrder: S(() =>
            displayedOrder() &&
            displayedOrder()!.OriginalOrder() &&
            OriginalPortCallViewModel(displayedOrder()!.OriginalOrder()!)
        ),
        ShowOrderUnavailableMsg: showOrderUnavailableMsg,
        Loading: loading,
        LinkAvailable,
        PrintOrder
    };

    function LinkAvailable() {
        // No sense offering a print pilot order link unless/until we have a valid PortCallId.
        return pjo.PortCallId() > 0;
    }

    async function PrintOrder() {
        if (loading()) return;
        loading(true);
        try {
            displayedOrder(await LoadOriginalOrder());
            try {
                window.print();
            } finally {
                displayedOrder(null);
            }
        } finally {
            S.freeze(() => {
                loading(false);
                hideActionsMenu();
            });
        }
    }

    // NOTE: I originally tried using a CachedModelLoader here instead of this function.
    // Using this function instead so we can implement custom error handling logic based on
    // the response status. CachedModelLoader doesn't currently expose the error response
    // (well, unless you check ajax.LastResponse, but that makes it hard to know you've got the
    // right response).
    async function LoadOriginalOrder() {
        const req = client.Get();
        try {
            const orderData = await req;
            return cache.OriginalPilotOrders.update1(req.Date, orderData);
        } catch (ex) {
            const resp = req.Response();
            // 404 signals that we couldn't find the original order data for this PortCall.
            // That most likely means the PortCall predates our version tables. Show an
            // appropriate error message.
            if (resp && resp.Status === 404) {
                showOrderUnavailableMsg(true);
            }
            throw ex;
        }
    }
}

export type OriginalPortCallViewModel = ReturnType<typeof OriginalPortCallViewModel>;
function OriginalPortCallViewModel(
    pc : PortCall
) {
    const
        lengthUOM = () => pc.Port() && pc.Port()!.PierLengthUOM() || UOM.Foot,
        depthUOM = () => pc.Port() && pc.Port()!.PierDepthUOM() || UOM.Foot,
        pjos = pc.JobOrders
            .map(jo => jo.PilotJobOrder()!)
            .filter(pjo => pjo !== null),
        pjosByMovement = nonuniqueIndex(pjos, pjo => movementKey(pjo.FromPointId(), pjo.ToPointId())),
        itinerary = pc.Itinerary.sort(ItineraryPoint.EffectiveOrder),
        movements = S(() => {
            const _itinerary = itinerary();
            return _itinerary.slice(0, -1).map((fromPt, idx) => {
                const toPt = _itinerary[idx + 1];
                return MovementViewModel(fromPt, toPt, pjosByMovement, depthUOM);
            });
        }),
        // For certain PJO fields (e.g. Bill To, Tugs, Remarks, others), the AddPortCall form
        // uses a single input field to set the initial value for multiple PJOs at once. For
        // most such fields, all PJOs for that PortCall get matching initial values. The one
        // exception is labor start time: That one's presented as a single input field like the
        // others, but our business rules say to apply the supplied value to the IN PJO only. So,
        // since we're trying to recreate the AddPortCall form as the user originally entered it,
        // we'll take the 1st PJO in movement order as definitive for all of these fields.
        typicalPJO = S(() => {
            const pjos = movements().map(mvmt => mvmt.PilotJobOrder()!).filter(pjo => pjo !== null);
            return pjos.length ? pjos[0] : null;
        });

    return {
        Model: pc,
        LengthDisplay: lengthDisplay(pc.VesselLength, lengthUOM),
        Vessel: S(() => pc.Vessel() && OriginalVesselViewModel(pc.Vessel()!, lengthUOM)),
        Movements: movements,
        TypicalPilotJobOrder: typicalPJO
    };
}

export type OriginalVesselViewModel = ReturnType<typeof OriginalVesselViewModel>;
function OriginalVesselViewModel(
    vessel : Vessel,
    lengthUOM : () => string
) {
    return {
        Model: vessel,
        BeamDisplay: lengthDisplay(vessel.Beam, lengthUOM),
        GrossTonnageDisplay: () => vessel.GrossTonnage.toString({ units: false })
    };
}

export type MovementViewModel = ReturnType<typeof MovementViewModel>;
function MovementViewModel(
    fromPt : ItineraryPoint,
    toPt : ItineraryPoint,
    pjosByMovement : NonuniqueIndex<PilotJobOrder>,
    depthUOM : () => string
) {
    const
        key = () => movementKey(fromPt.Id(), toPt.Id()),
        // To reservation on INs and SHIFTs, from reservation on OUTs
        referenceReservation = () => toPt.BerthReservation() || fromPt.BerthReservation(),
        pjo = S(() => {
            const all = pjosByMovement(key())();
            return all.length ? all[0] : null;
        });

    return {
        Key: key,
        FromPoint: fromPt,
        ToPoint: toPt,
        // Ditto DispatchDashboard on how this determination of movement type won't hold up
        // if we start modeling things like taking multiple stops from SEA to the reserved berth.
        Type: () => fromPt.BerthReservation() ?
            toPt.BerthReservation() ?
                'shift' :
                'out' :
            'in',
        MovementTime: fromPt.Departure.Effective,
        Reservation: S(() => referenceReservation() && ReservationViewModel(referenceReservation()!)),
        PilotJobOrder: S(() => pjo() && OriginalPilotJobOrderViewModel(pjo()!, depthUOM))
    };
}

export type ReservationViewModel = ReturnType<typeof ReservationViewModel>;
function ReservationViewModel(
    reservation : BerthReservation
) {
    return {
        // Rounding behavior intended to match the FootmarkAssignments widget
        FootmarkNum: () => Math.round(reservation.Footmark.Num()),
        EndmarkNum: () => Math.round(reservation.EndMark.Num()),
        SideToText: () =>
            reservation.SideTo() === VesselSide.Starboard ? 'Starboard' :
            reservation.SideTo() === VesselSide.Port ? 'Port' :
            VesselSideDisplayName[reservation.SideTo()]
    };
}

export type OriginalPilotJobOrderViewModel = ReturnType<typeof OriginalPilotJobOrderViewModel>;
function OriginalPilotJobOrderViewModel(
    pjo : PilotJobOrder,
    depthUOM : () => string
) {
    return {
        Model: pjo,
        AirDraftDisplay: draftDisplay(pjo.AirDraft, depthUOM),
        DeepDraftDisplay: draftDisplay(pjo.DeepDraft, depthUOM)
    };
}

function lengthDisplay(m : NullableMeasurement, uom : () => string) {
    return () => uom() === UOM.Foot ?
        new MeasurementInFeetAndInches(m, 0)
            .toString({ unitsStyle: 'abbrev', padInchesLeft: false }) :
        m.convert(UOM.Meter).roundToNPlaces(0).toString();
}

function draftDisplay(m : NullableMeasurement, uom : () => string) {
    return () => uom() === UOM.Foot ?
        new MeasurementInFeetAndInches(m, 0)
            .toString({ unitsStyle: 'pretty-ticks', padInchesLeft: false }) :
        m.convert(UOM.Meter).roundToNPlaces(2).toString();
}

function movementKey(fromPointId : number, toPointId : number) {
    return `${fromPointId}:${toPointId}`;
}



// WEBPACK FOOTER //
// ./apps/pilots/pilotjoborderdetails/originalpilotorder.ts